<template>
  <ion-page>
    <ion-content class="login-content">
      <div class="login-container">
        <div class="login-card">
          <div class="login-header">
            <h1>WalkyWalky</h1>
            <p>Enter your email and access code to continue</p>
          </div>
          
          <form @submit.prevent="handleLogin" class="login-form" autocomplete="on">
            <div class="input-group">
              <ion-label position="stacked">Email</ion-label>
              <ion-input
                v-model="email"
                type="email"
                name="email"
                id="email"
                placeholder="Enter your email"
                autocomplete="email"
                :class="{ 'ion-invalid': emailError }"
                @ion-input="clearError('email')"
                required
              ></ion-input>
              <ion-note v-if="emailError" color="danger">{{ emailError }}</ion-note>
            </div>
            
            <div class="input-group">
              <ion-label position="stacked">Access Code</ion-label>
              <ion-input
                v-model="code"
                type="text"
                placeholder="Enter 6-digit code"
                maxlength="6"
                :class="{ 'ion-invalid': codeError }"
                @ion-input="clearError('code')"
                required
              ></ion-input>
              <ion-note v-if="codeError" color="danger">{{ codeError }}</ion-note>
            </div>
            
            <ion-button 
              type="submit" 
              expand="block" 
              :disabled="isLoading || !isFormValid"
              class="login-button"
            >
              <ion-spinner v-if="isLoading" name="crescent"></ion-spinner>
              <span v-else>Login</span>
            </ion-button>
          </form>
          
          <div v-if="generalError" class="error-message">
            <ion-icon name="alert-circle"></ion-icon>
            {{ generalError }}
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { 
  IonPage, 
  IonContent, 
  IonInput, 
  IonLabel, 
  IonButton, 
  IonNote,
  IonIcon,
  IonSpinner
} from '@ionic/vue'
import { useRouter } from 'vue-router'
import { AuthService } from '../services/AuthService'

const router = useRouter()

// Form data
const email = ref('')
const code = ref('')
const isLoading = ref(false)

// Error states
const emailError = ref('')
const codeError = ref('')
const generalError = ref('')

// Form validation
const isFormValid = computed(() => {
  return email.value.trim() !== '' && 
         code.value.trim() !== '' && 
         code.value.length === 6 &&
         !emailError.value && 
         !codeError.value
})

// Clear specific error
const clearError = (field: string) => {
  if (field === 'email') emailError.value = ''
  if (field === 'code') codeError.value = ''
  generalError.value = ''
}

// Validate form
const validateForm = () => {
  let isValid = true
  
  // Clear previous errors
  emailError.value = ''
  codeError.value = ''
  generalError.value = ''
  
  // Validate email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!email.value.trim()) {
    emailError.value = 'Email is required'
    isValid = false
  } else if (!emailRegex.test(email.value)) {
    emailError.value = 'Please enter a valid email'
    isValid = false
  }
  
  // Validate code
  if (!code.value.trim()) {
    codeError.value = 'Access code is required'
    isValid = false
  } else if (code.value.length !== 6) {
    codeError.value = 'Code must be 6 digits'
    isValid = false
  } else if (!/^\d{6}$/.test(code.value)) {
    codeError.value = 'Code must contain only numbers'
    isValid = false
  }
  
  return isValid
}

// Handle login
const handleLogin = async () => {
  if (!validateForm()) return
  
  isLoading.value = true
  generalError.value = ''

  console.log('Email:', email.value);
  
  try {
    const success = await AuthService.login(email.value.trim(), code.value.trim())
    
    if (success) {
      // Redirect to main app
      router.push('/tabs')
    } else {
      generalError.value = 'Invalid email or code. Please try again.'
    }
  } catch (error: any) {
    console.error('HandleLogin error:', error)
    generalError.value = error.message || 'Login failed. Please try again.'
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.login-content {
  --background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  --padding-start: 0;
  --padding-end: 0;
  --padding-top: 0;
  --padding-bottom: 0;
}

.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.login-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  animation: slideUp 0.3s ease-out;
}

.login-header {
  text-align: center;
  margin-bottom: 32px;
}

.login-header h1 {
  color: #2c3e50;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.login-header p {
  color: #6c757d;
  font-size: 16px;
  margin: 0;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

ion-label {
  color: #495057;
  font-weight: 500;
  font-size: 14px;
}

ion-input {
  --background: #f8f9fa;
  --border-radius: 8px;
  --padding-start: 16px;
  --padding-end: 16px;
  --padding-top: 12px;
  --padding-bottom: 12px;
  border: 1px solid #e9ecef;
  font-size: 16px;
}

ion-input.ion-invalid {
  --border-color: #dc3545;
  border-color: #dc3545;
}

ion-note {
  font-size: 12px;
  margin-top: 4px;
}

.login-button {
  --background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  --background-hover: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
  --border-radius: 8px;
  --padding-top: 16px;
  --padding-bottom: 16px;
  margin-top: 8px;
  font-weight: 600;
  font-size: 16px;
  height: 48px;
}

.login-button:disabled {
  --background: #e9ecef;
  --color: #6c757d;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8d7da;
  color: #721c24;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #f5c6cb;
  margin-top: 16px;
  font-size: 14px;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
